import { useState, useEffect } from "react";
import sleep from "sleep-promise";
import Modal from '../../../../core/components/core/Util/Modal/CustomModal';
import CustomForm from '../../../../core/components/core/Util/Form/CustomForm/CustomForm';
import config from '../../../config/config';
import { chooseProfileFilter } from '../../../helpers/chooseProfileFilter';
import Fetch from "../../../../core/util/core/Fetch";

function PayExpense({ show, onClose, expenseId }) {
    const profileFilter = chooseProfileFilter();

    const [form, setForm] = useState();
    const [formContent, setFormContent] = useState(<>Carregando...</>);
    const [loading, setLoading] = useState(false);

    const endpointBackend = config.endpoints.backend;

    const paths = {
        endpointSave: `${endpointBackend}gci/expenses/pay`,
        endpointGetAllDocumentTypes: `${endpointBackend}gci/revenues-document-types/getall`,
        endpointGetAllBankAccounts: `${endpointBackend}gci/bank-accounts/getall`,
        endpointGetPaymentsInfoByExpenseId: `${endpointBackend}gci/expenses-payments/getPaymentsInfoByExpenseId`,
    };

    const getDocumentNumberRequired = async (values) => {
        const documentTypeId = values?.gci_revenue_document_type_id;
        if (!documentTypeId) return false;
        const documentType = await Fetch.post(`${endpointBackend}gci/revenues-document-types/getById`, { id: documentTypeId });
        return documentType?.need_document_number === '1';
    };

    // ---------- FORM CONFIG ----------
    const getForm = (values, documentNumberRequired = false) => {
        const form = {
            afterSubmit: (form, values) => {
                onClose();
            },
            hiddenValues: {
                gci_expense_id: expenseId,
                status: 'paid',
            },
            fields: [
                {
                    name: 'payment_date',
                    type: 'date',
                    label: 'Data de Pagamento',
                    validations: { required: true },
                    columnSize: 12
                },
                {
                    name: 'remaining_value',
                    type: 'decimal',
                    label: 'Valor Restante',
                    validations: { required: false },
                    readOnly: true,
                    columnSize: 12
                },
                {
                    name: 'value',
                    type: 'decimal',
                    label: 'Valor Pago',
                    validations: {
                        required: true,
                        validateValue: true
                    },
                    columnSize: 12
                },
                {
                    name: 'gci_revenue_document_type_id',
                    type: 'select',
                    label: 'Tipo Doc',
                    options: [],
                    optionsEndpoint: {
                        endpoint: paths.endpointGetAllDocumentTypes,
                        label: 'name',
                        value: 'id',
                    },
                    validations: { required: false },
                    columnSize: 12,
                    handleChange: async (element, field, values) => {
                        const docNumberRequired = await getDocumentNumberRequired(values);
                        const updatedForm = getForm(values, docNumberRequired);
                        updatedForm.values = values;
                        setForm(updatedForm);
                        setFormContent(<></>);
                        await sleep(1);
                        setFormContent(<CustomForm form={updatedForm} />);
                    }
                },
                {
                    name: 'document_number',
                    type: 'text',
                    label: 'Número do Doc',
                    validations: { required: documentNumberRequired },
                    columnSize: 12
                },
                {
                    name: 'gci_bank_account_id',
                    type: 'select',
                    label: 'Conta Bancária',
                    options: [],
                    optionsEndpoint: {
                        endpoint: paths.endpointGetAllBankAccounts,
                        label: 'full_name',
                        value: 'id',
                        params: {
                            filter: { ...profileFilter }
                        }
                    },
                    validations: { required: false },
                    columnSize: 12
                },
            ],
            customValidators: {
                validateValue: (value, options, key, attributes) => {
                    const numericValue = parseFloat(value || 0);
                    const remaining = parseFloat(attributes.remaining_value);

                    if (numericValue <= 0) {
                        return '^O valor pago deve ser maior que 0.';
                    } else if (numericValue > remaining) {
                        return '^O valor pago não pode ser maior que o valor restante.';
                    }

                    return '';
                }
            },
            labelSubmitButton: "Confirmar Pagamento",
            showGoBackButton: false,
            ...paths,
        };

        form.values = values;
        return form;
    };

    // ---------- LOAD FORM ----------
    useEffect(() => {
        const load = async () => {
            if (!show) return;

            setLoading(true);

            const valuesPayment = await Fetch.post(`${paths.endpointGetPaymentsInfoByExpenseId}`, {
                filter: { gci_expense_id: expenseId }
            });

            const initialValues = {
                payment_date: new Date(),
                value: valuesPayment.remaining_value,
                remaining_value: valuesPayment.remaining_value,
                id: null
            };

            const docNumberRequired = await getDocumentNumberRequired(initialValues);
            const form = getForm(initialValues, docNumberRequired);

            setForm(form);
            setFormContent(<CustomForm form={form} />);
            setLoading(false);
        };

        load();
    }, [show, expenseId]);

    return (
        <Modal
            show={show}
            handleShow={() => { }}
            handleClose={onClose}
            handleConfirm={() => { }}
            title="Registrar Pagamento de Despesa"
            content={loading ? "Carregando..." : formContent}
            showButtons={false}
            showHeader={true}
        />
    );
}

export default PayExpense;
